// "Esto me lo llevo sí o sí."
//
// POR QUÉ ESTA PANTALLA CAMBIA LA FUNCIÓN
//
// Hasta ahora la valija iba en una sola dirección: le decías las
// ocasiones y elegía todo desde cero. Es útil, pero no es como uno arma
// una valija de verdad. En la vida real se empieza al revés — primero
// el saco que querés usar en la cena del jueves, y después lo que lo
// acompaña.
//
// LO QUE NO HACE, A PROPÓSITO
//
// No reemplaza al algoritmo. Lo que elegís es el punto de partida, no
// la valija: la app sigue completando lo que falta y sigue mostrando
// cuántos días se usa cada prenda. Si quisieras armarla entera a mano,
// esta pantalla sobra y la que sirve es "Armar a mano".
//
// EL BUSCADOR NO ES UN LUJO
//
// Con 166 prendas, una grilla sin buscar es una grilla que no se usa.
// Es el mismo `filtrarPrendas` de "Qué me pongo" y del armador: buscar
// por nombre, tipo, material o color, sin tildes.

import { useCallback, useEffect, useMemo, useState } from "react";
import * as api from "./api";
import { filtrarPrendas } from "./seleccion";
import { fotoChica } from "./foto";
import { t, tError, tPlural, useIdioma } from "./i18n";

// Cuántas se muestran cuando no hay búsqueda.
//
// No están TODAS por ancho de banda: cada una es una foto firmada, y
// 166 fotos para elegir tres es exactamente el problema que el resto de
// la app viene cuidando. Se muestran las que hace más tiempo no usás
// —que es de donde uno saca "ah, esto me lo llevo"— y el resto aparece
// buscando.
const SIN_BUSCAR = 12;
const BUSCANDO = 24;

export default function ElegirPrendas({ elegidas, onCambiar }) {
  /* Se repinta cuando llega el idioma. Sin esto el componente depende
   * de que su padre se repinte por otra razón — hoy pasa, y por eso el
   * bug del 2026-08-25 se veía en unas pantallas y no en otras. */
  useIdioma();
  const [armario, setArmario] = useState(null);
  const [texto, setTexto] = useState("");
  const [error, setError] = useState("");

  const traer = useCallback(async () => {
    try {
      const d = await api.listarPrendas({ orden: "olvidadas" });
      setArmario(d.prendas || []);
    } catch (e) {
      setError(tError(e));
    }
  }, []);

  useEffect(() => {
    traer();
  }, [traer]);

  const porId = useMemo(
    () => new Map((armario || []).map((p) => [p.id, p])),
    [armario]
  );

  const visibles = useMemo(() => {
    if (!armario) return [];
    const filtradas = filtrarPrendas(armario, { texto });
    return filtradas.slice(0, texto ? BUSCANDO : SIN_BUSCAR);
  }, [armario, texto]);

  function alternar(id) {
    onCambiar(
      elegidas.includes(id)
        ? elegidas.filter((x) => x !== id)
        : [...elegidas, id]
    );
  }

  if (error) return <div className="mensaje-error texto-mini" role="alert">{error}</div>;
  if (!armario) return <p className="tenue texto-mini">{t("elegir.cargando", "Cargando tu armario...")}</p>;

  // Las elegidas van SIEMPRE arriba y completas, aunque no estén en la
  // página visible ni coincidan con la búsqueda. Si al escribir en el
  // buscador desapareciera lo que ya marcaste, parecería que se
  // desmarcó.
  const marcadas = elegidas.map((id) => porId.get(id)).filter(Boolean);

  return (
    <div className="elegir-prendas">
      {marcadas.length > 0 && (
        <div className="elegidas-fila">
          <p className="filtro-titulo">
            {t("elegir.teLlevas", "Te llevas {prendas} sí o sí", {
              prendas: tPlural("plural.prendas", marcadas.length),
            })}
          </p>
          <div className="etiquetas">
            {marcadas.map((p) => (
              <button
                key={p.id}
                type="button"
                className="etiqueta etiqueta-quitar"
                onClick={() => alternar(p.id)}
                title={t("elegir.sacar", "Sacar de la lista")}
              >
                {p.nombre} ×
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="buscador">
        <input
          type="search"
          value={texto}
          onChange={(e) => setTexto(e.target.value)}
          placeholder={t("elegir.buscar", "Buscar en tu armario...")}
        />
      </div>

      <div className="grilla-elegir">
        {visibles.map((p) => (
          <button
            key={p.id}
            type="button"
            className={`elegir-prenda ${
              elegidas.includes(p.id) ? "elegir-prenda-marcada" : ""
            }`}
            onClick={() => alternar(p.id)}
            title={p.nombre}
          >
            {fotoChica(p) ? (
              <img loading="lazy" decoding="async" src={fotoChica(p)} alt={p.nombre} className="prenda-foto" />
            ) : (
              <div className="prenda-foto prenda-sin-foto">
                <span className="tenue texto-mini">{t("elegir.sinFoto", "Sin foto")}</span>
              </div>
            )}
            <span className="texto-mini elegir-nombre">{p.nombre}</span>
          </button>
        ))}
      </div>

      {visibles.length === 0 && (
        <p className="tenue texto-mini">{t("elegir.sinCoincidencias", "Ninguna prenda coincide con esa búsqueda.")}</p>
      )}

      {!texto && armario.length > SIN_BUSCAR && (
        <p className="tenue texto-mini" style={{ margin: "6px 0 0" }}>
          {t("elegir.seMuestran", "Se muestran las {n} que hace más tiempo no usas. Busca para ver el resto.", {
            n: SIN_BUSCAR,
          })}
        </p>
      )}
    </div>
  );
}
